import { indigo, yellow } from "@mui/material/colors";
import { PaletteColor, PaletteColorOptions, createTheme } from "@mui/material/styles";

export const getItemColor = (isPet: boolean) => isPet ? {
    hover: indigo[200],
    selected: indigo[100],
    hoverAndSelected: indigo[300],
} : {
    hover: "#9ddbb0",
    selected: "#c4e9ce",
    hoverAndSelected: "#73ce90",
};

export const customerAddressHistoryChangedFieldBackgroundColor = yellow[100];

export const getTheme = (isPet: boolean) => {
    const itemColor = getItemColor(isPet);
    return createTheme({
        palette: {
            mode: "light",
            primary: {
                main: isPet ? indigo[800] : "#00853f",
            },
            secondary: {
                main: "#D10012",
            },
            positive: {
                main: "#00C000",
            },
            negative: {
                main: "#D10012",
            },
            save: {
                main: "#00C000",
            },
            add: {
                main: "#00C000",
            },
            delete: {
                main: "#D10012",
            },
            edit: {
                main: yellow[700],
            },
        },
        mixins: {
            toolbar: {
                minHeight: 24,
                "@media (min-width:0px) and (orientation: landscape)": {
                    minHeight: 24
                },
                "@media (min-width:600px)": {
                    minHeight: 24
                }
            }
        },
        spacing: 4,
        shape: {
            borderRadius: 2,
        },
        typography: {
            htmlFontSize: 18,
        },
        components: {
            MuiInputLabel: {
                defaultProps: {
                    shrink: true
                }
            },
            MuiOutlinedInput: {
                defaultProps: {
                    notched: true // needed for above "shrink: true" to avoid borders crossing the labels
                }
            },
            MuiTextField: {
                defaultProps: {
                    fullWidth: true,
                    autoComplete: "off"
                }
            },
            MuiButton: {
                defaultProps: {
                    variant: "contained",
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        padding: "8px",
                    }
                }
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        overflowY: "visible",
                        padding: "4px 8px",
                    }
                }
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        margin: "7px 0 8px 0",
                    }
                },
                defaultProps: {
                    size: "small",
                    fullWidth: true
                }
            },
            MuiCard: {
                defaultProps: {
                    variant: "outlined",
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        "&:hover:not(.Mui-selected)": {
                            backgroundColor: `${itemColor.hover} !important`,
                        },
                        "&.Mui-selected:not(:hover)": {
                            backgroundColor: `${itemColor.selected} !important`,
                        },
                        "&.Mui-selected:hover": {
                            backgroundColor: `${itemColor.hoverAndSelected} !important`,
                        },
                    }
                }
            },
            MuiAutocomplete: {
                styleOverrides: {
                    option: {
                        "&:hover:not([aria-selected='true'])": {
                            backgroundColor: `${itemColor.hover} !important`,
                        },
                        "&[aria-selected='true']:not(:hover)": {
                            backgroundColor: `${itemColor.selected} !important`,
                        },
                        "&[aria-selected='true']:hover": {
                            backgroundColor: `${itemColor.hoverAndSelected} !important`,
                        },
                    }
                }
            }
        },
    });
};

declare module "@mui/material/styles" {
    interface PaletteOptions {
        positive: PaletteColorOptions;
        negative: PaletteColorOptions;
        save: PaletteColorOptions;
        add: PaletteColorOptions;
        delete: PaletteColorOptions;
        edit: PaletteColorOptions;
    }
    interface Palette {
        positive: PaletteColor;
        negative: PaletteColor;
        save: PaletteColor;
        add: PaletteColor;
        delete: PaletteColor;
        edit: PaletteColor;
    }
}

declare module "@mui/material/Button" {
    interface ButtonPropsColorOverrides {
        positive: true;
        negative: true;
        save: true;
        add: true;
        delete: true;
        edit: true;
    }
}

declare module "@mui/material/TextField" {
    interface TextFieldPropsColorOverrides {
        positive: true;
        negative: true;
        save: true;
        add: true;
        delete: true;
        edit: true;
    }
}
